import Vue from 'vue';
import { Instance, instanceSettings } from '@ekd/portal-connector/dist/Instance';

import { InstanceContext, Instances } from '@root/common/types/instanceConnector';

import { createPaywallService } from '@paywall/services/paywall.service';
import { Subscription } from '@paywall/types';

type PaywallPromise = [{ value?: boolean }, { value?: { access: boolean } }, { value: Subscription[] }];
async function fetchPaywallData(
  paywallService: ReturnType<typeof createPaywallService>,
  { token, channelId, fakeChannel }: { token: string; channelId: string | number; fakeChannel: boolean }
) {
  const [adFree, channelAccess, subscription] = (await Promise.allSettled([
    paywallService.checkAdFree(token),
    fakeChannel
      ? paywallService.checkAccessInternal(token)
      : paywallService.checkAccess({ articleId: '', channelId: String(channelId), token, type: 'Customer' }),
    paywallService.getSubscription(token),
  ])) as PaywallPromise;

  return {
    adFree: typeof adFree?.value === 'boolean' ? adFree.value : false,
    hasChannelAccess: typeof channelAccess?.value?.access === 'boolean' ? channelAccess.value.access : false,
    subscription: subscription.value,
  };
}

@instanceSettings({ name: 'paywall' })
export class PaywallInstance extends Instance<InstanceContext, Instances> {
  public state = Vue.observable({
    adFree: false,
    hasChannelAccess: false,
    subscription: [] as Subscription[],
  });

  public paywallService!: ReturnType<typeof createPaywallService>;

  public changePaywallState() {
    this.instances.paywall.state.adFree = false;
  }

  public init({ adFreeApp, channelId, fakeChannel }: { adFreeApp: boolean; channelId: string | number; fakeChannel: boolean }) {
    const isServer = typeof window === 'undefined';

    if (!(this.context.service || this.context.store || this.context.$eventHandler)) {
      return;
    }

    if (!this.paywallService) {
      this.paywallService = createPaywallService({ service: this.context.service });
    }

    this.state.adFree = adFreeApp;

    if (isServer) {
      return;
    }

    this.context.store.watch(
      (state: any) => state.customer.token,
      async (token: string) => {
        const { adFree, hasChannelAccess, subscription } = await fetchPaywallData(this.paywallService, { token, channelId, fakeChannel });
        this.state.adFree = adFreeApp || adFree;
        this.state.hasChannelAccess = hasChannelAccess;
        this.state.subscription = subscription;
        this.context.$eventHandler.emit('paywall-state', this.state);
      }
    );
  }
}
